import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { MILESTONES, TESTIMONIALS, ADVISOR_RESULT } from "@/data/content";
import { SectionLabel, CountUp } from "@/components/site/primitives";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trackClick } from "@/lib/utils";

const stripVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
};

const chipVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export const Traction = () => {
  return (
    <section
      id="traction"
      data-testid="traction-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 07 ]" testId="traction-label">
        Traction & Milestones
      </SectionLabel>

      <div className="relative pl-8 md:pl-0">
        {/* vertical line */}
        <div className="absolute left-[3px] md:left-1/2 top-0 bottom-0 w-px bg-epic-line md:-translate-x-1/2" />

        {MILESTONES.map((m, i) => (
          <motion.div
            key={m.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className={`relative grid md:grid-cols-2 gap-4 md:gap-16 py-8 md:py-12 ${
              i % 2 === 0 ? "" : "md:text-right"
            }`}
            data-testid={`milestone-${i}`}
          >
            {/* dot */}
            <span
              className={`absolute left-[-30px] md:left-1/2 top-10 md:top-14 w-3 h-3 rounded-full bg-epic-accent md:-translate-x-1/2 ${
                i === MILESTONES.length - 1 ? "ring-4 ring-epic-accent/25 animate-pulse" : ""
              }`}
            />
            <div className={i % 2 === 0 ? "md:pr-16" : "md:col-start-2 md:pl-16"}>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-accent mb-3">
                {m.date}
              </p>
              <h3 className="font-grotesk font-bold uppercase tracking-tight text-2xl md:text-3xl text-epic-text mb-3">
                {m.title}
              </h3>
              {(m.stats || m.statText) && (
                <p
                  data-testid={`milestone-stat-${i}`}
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-text border-l-2 border-epic-accent pl-3 mb-4"
                >
                  {m.stats
                    ? m.stats.map((s, j) => (
                        <span key={s.label}>
                          {j > 0 && <span className="text-epic-sub mx-2">·</span>}
                          <CountUp {...s} />
                        </span>
                      ))
                    : m.statText}
                </p>
              )}
              <p className="font-mono text-sm text-epic-sub leading-relaxed">{m.body}</p>
              {m.partners && (
                <motion.div
                  data-testid="partner-strip"
                  variants={stripVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className={`flex flex-wrap gap-2 mt-5 ${i % 2 === 0 ? "" : "md:justify-end"}`}
                >
                  {m.partners.map((p) => {
                    const slug = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return (
                      <Popover key={p.name}>
                        <PopoverTrigger asChild>
                          <motion.button
                            variants={chipVariants}
                            data-testid={`partner-${slug}`}
                            className="border border-epic-line px-3.5 py-2 font-grotesk text-xs md:text-sm font-semibold uppercase tracking-wide text-epic-sub transition-colors duration-300 hover:border-epic-accent hover:text-epic-text cursor-pointer"
                          >
                            {p.name}
                          </motion.button>
                        </PopoverTrigger>
                        <PopoverContent
                          data-testid={`partner-popup-${slug}`}
                          side="top"
                          className="w-64 rounded-none bg-epic-bg border-epic-line p-4"
                        >
                          <p className="font-grotesk font-bold uppercase tracking-wide text-sm text-epic-text mb-1.5">
                            {p.name}
                          </p>
                          <p className="font-mono text-xs leading-relaxed text-epic-sub">{p.desc}</p>
                          {p.url && (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`partner-visit-${slug}`}
                              onClick={() => trackClick(`partner-${slug}`)}
                              className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent transition-colors duration-200 hover:text-epic-text"
                            >
                              Visit site
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advisor results spotlight */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mt-20 md:mt-28 border border-epic-line border-l-2 border-l-epic-accent bg-epic-surface p-6 md:p-10"
        data-testid="advisor-result"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-accent mb-8">
          Advisor results — first seminar week
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {ADVISOR_RESULT.stats.map((s, i) => (
            <div key={s.label} data-testid={`advisor-stat-${i}`}>
              <p className="font-grotesk font-black text-4xl md:text-5xl text-epic-text tracking-tighter">
                <CountUp value={s.value} />
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub mt-2">
                {s.label}
              </p>
              {s.note && (
                <p className="font-mono text-[10px] text-epic-accent mt-1">{s.note}</p>
              )}
            </div>
          ))}
        </div>
        <blockquote className="font-editorial italic text-xl md:text-2xl leading-snug text-epic-text max-w-3xl">
          &ldquo;{ADVISOR_RESULT.quote}&rdquo;
        </blockquote>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mt-4">
          {ADVISOR_RESULT.name} · {ADVISOR_RESULT.role}
        </p>
      </motion.div>

      {/* Advisor testimonials */}
      <div className="mt-4 md:mt-6">
        <p
          data-testid="testimonials-label"
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-8"
        >
          What advisors say
        </p>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          data-testid="testimonials-strip"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              className="border border-epic-line bg-epic-surface p-6 md:p-7 flex flex-col transition-colors duration-300 hover:border-epic-lineHover"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-3.5 h-3.5 fill-epic-accent text-epic-accent" />
                ))}
              </div>
              <blockquote className="font-mono text-sm text-epic-text leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 pt-4 border-t border-epic-line">
                <p className="font-grotesk font-bold uppercase tracking-tight text-sm text-epic-text">
                  {t.name}
                </p>
                <p className="font-mono text-[11px] text-epic-sub mt-1">{t.role}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
